// src/auth/AuthManager.ts
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

// Provider-agnostic OIDC config. For Cognito, authority is the User Pool URL;
// for Keycloak it will be the realm URL. oidc-client-ts auto-discovers most
// endpoints from {authority}/.well-known/openid-configuration.
const authority = process.env.REACT_APP_OIDC_AUTHORITY!;
const clientId = process.env.REACT_APP_OIDC_CLIENT_ID!;
const redirectUri = process.env.REACT_APP_OIDC_REDIRECT_URI!;
const scope = process.env.REACT_APP_OIDC_SCOPE!;
const appBaseUrl = process.env.REACT_APP_BASE_URL!;

// Provider-specific hosted UI / endpoint base. Cognito needs explicit endpoint
// overrides because its /.well-known does not expose the hosted-UI logout
// correctly. When migrating to Keycloak, this whole metadata override block can
// likely be removed since Keycloak's discovery document is standards-compliant.
const oidcEndpointBase = process.env.REACT_APP_OIDC_ENDPOINT_BASE!;

export const userManager = new UserManager({
	authority,
	client_id: clientId,
	redirect_uri: redirectUri,
	post_logout_redirect_uri: appBaseUrl,
	response_type: 'code',
	scope,

	// PKCE is on by default in oidc-client-ts — no extra config needed

	// Store OIDC state (code_verifier, nonce etc.) in sessionStorage
	// This is cleared when the tab closes — appropriate for security
	userStore: new WebStorageStateStore({ store: window.sessionStorage }),

	// --- PROVIDER-SPECIFIC: Cognito endpoint overrides ---
	// Cognito's hosted UI does not expose a standards-compliant discovery
	// document for the logout endpoint, so we override the endpoints explicitly.
	// For Keycloak, this metadata block can be removed - discovery handles it.
	metadata: {
		issuer: authority,
		authorization_endpoint: `${oidcEndpointBase}/oauth2/authorize`,
		token_endpoint: `${oidcEndpointBase}/oauth2/token`,
		// We are no longer calling signoutRedirect
		// end_session_endpoint: `${oidcEndpointBase}/logout`,
		revocation_endpoint: `${oidcEndpointBase}/oauth2/revoke`,
		jwks_uri: `${authority}/.well-known/jwks.json`,
	},

	// Automatically refresh the access token 60 seconds before it expires
	// Use refresh token grant for silent renewal — never use iframes
	automaticSilentRenew: true,

	// Reduce the silent renew timeout so a fallback failure isn't a 10s wait
	silentRequestTimeoutInSeconds: 3,

	// --- PROVIDER-SPECIFIC: Cognito token revocation ---
	// Cognito does not support the silent renew iframe method (no prompt=none
	// on the Hosted UI), and only accepts refresh_token for revocation.
	// Keycloak supports both, so revokeTokenTypes may be expanded at migration.
	revokeTokensOnSignout: true,
	revokeTokenTypes: ['refresh_token'],
});
